import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import {
  apiBase,
  aud,
  bidderLabel,
  type AuctionView
} from './auction';
import AuctionCountdown from './AuctionCountdown';

/* The live auction on the public homepage: the lot, the leading bid and who holds
 * it, the clock, and one door — bidding happens in the portal. A bare fetch with no
 * credentials, like the card catalog: the page is the same for everyone, and a 401
 * through the authed client would send a visitor into the refresh/logout path.
 * Nothing is rendered until the read answers, and nothing at all when there is no
 * auction to show, so the landing page is unchanged between auctions. */
const GOLD = '#e8c877';

/* `tight`: inside another page (the public Physical Cards storefront) rather than
 * between the landing page's sections — less air above and below. */
export const AuctionHomeSection: React.FC<{ tight?: boolean }> = ({
  tight
}) => {
  const [view, setView] = useState<AuctionView | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    const load = () =>
      fetch(`${apiBase()}/auctions/current`, {
        credentials: 'omit',
        signal: ctrl.signal
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: AuctionView | null) => {
          if (d) setView(d);
        })
        .catch(() => {
          /* the section simply stays hidden */
        });
    load();
    const t = window.setInterval(load, 30000);
    return () => {
      ctrl.abort();
      window.clearInterval(t);
    };
  }, []);

  const lot = view?.auction;
  if (!lot || lot.status === 'cancelled' || lot.status === 'draft') return null;
  const top = view?.top?.[0] || null;
  const open = lot.live;

  const closes = new Date(lot.endsAt).toLocaleString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit'
  });
  const mono = '800 10px ui-monospace, Menlo, Consolas, monospace';

  return (
    <Box
      component="section"
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: tight ? 0 : { xs: 2.5, md: 3 },
        pt: tight ? 1 : { xs: 6, md: 9 },
        pb: tight ? { xs: 5, md: 7 } : { xs: 1, md: 2 },
        // inside the storefront a rule and room below keep the auction from reading
        // as part of the pack drop that follows ("582 auctions left")
        mb: tight ? { xs: 5, md: 7 } : 0,
        borderBottom: tight ? '1px solid rgba(140,170,190,0.14)' : 'none',
        '@keyframes incAuctionPulse': {
          '0%, 100%': { boxShadow: `0 0 0 0 ${GOLD}66` },
          '50%': { boxShadow: `0 0 0 6px ${GOLD}00` }
        }
      }}
    >
      {/* The lot sheet: a double hairline frame like a catalogue page, the artwork
          bleeding to the frame's edge with the live ribbon across its corner, and the
          particulars ruled off on the right the way a lot card lists them. */}
      <Box
        sx={{
          position: 'relative',
          borderRadius: '6px',
          border: `1px solid ${GOLD}66`,
          outline: `1px solid ${GOLD}2e`,
          outlineOffset: 5,
          background: `repeating-linear-gradient(135deg, ${GOLD}08 0 2px, transparent 2px 14px), linear-gradient(180deg, #0d141d, #070b11)`,
          boxShadow: `0 40px 90px -50px ${GOLD}88, inset 0 1px 0 rgba(255,255,255,0.04)`,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: lot.imageUrl ? '6fr 7fr' : '1fr'
          },
          overflow: 'hidden'
        }}
      >
        {lot.imageUrl && (
          <Box sx={{ position: 'relative', minHeight: { xs: 260, md: 440 } }}>
            <Box
              component="img"
              src={lot.imageUrl}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            {/* fade into the sheet on the side the particulars sit */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: {
                  xs: 'linear-gradient(180deg, transparent 55%, #070b11 100%)',
                  md: 'linear-gradient(90deg, transparent 70%, #070b11 100%)'
                }
              }}
            />
            {/* the ribbon */}
            <Box
              sx={{
                position: 'absolute',
                top: 26,
                left: -46,
                transform: 'rotate(-35deg)',
                width: 220,
                py: 0.6,
                textAlign: 'center',
                background: open ? GOLD : '#2a3340',
                color: open ? '#0b0f14' : '#cfd9e2',
                font: '800 10px ui-monospace, Menlo, Consolas, monospace',
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                boxShadow: '0 6px 18px rgba(0,0,0,0.5)'
              }}
            >
              {open
                ? 'Live auction'
                : lot.status === 'settled'
                  ? 'Sold'
                  : 'Closed'}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            p: { xs: 2.5, md: 4 },
            pt: { xs: 1.5, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25
          }}
        >
          <Typography
            sx={{
              font: mono,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: GOLD,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {open && (
              <Box
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: GOLD,
                  animation: 'incAuctionPulse 1.6s ease-out infinite'
                }}
              />
            )}
            First edition · one of one ·{' '}
            {open
              ? 'bidding open'
              : lot.status === 'settled'
                ? 'sold'
                : 'bidding closed'}
          </Typography>

          <Typography
            className="inc-orbitron"
            sx={{
              fontSize: { xs: 24, md: 30 },
              fontWeight: 800,
              color: '#eef6fb',
              lineHeight: 1.12,
              textWrap: 'balance'
            }}
          >
            {lot.title}
          </Typography>

          {lot.description && (
            <Typography
              sx={{
                color: '#9fb2c0',
                fontSize: { xs: 14.5, md: 15.5 },
                lineHeight: 1.6,
                whiteSpace: 'pre-line'
              }}
            >
              {lot.description}
            </Typography>
          )}

          {/* the particulars, ruled */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto 1fr' },
              gap: { xs: 2, sm: 3 },
              py: 2,
              borderTop: `1px solid ${GOLD}33`,
              borderBottom: `1px solid ${GOLD}33`
            }}
          >
            <Box>
              <Typography
                sx={{
                  font: mono,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#8ea3b3'
                }}
              >
                {top ? 'Leading bid' : 'Opening bid'}
              </Typography>
              <Typography
                className="inc-orbitron"
                sx={{
                  mt: 0.75,
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 800,
                  color: GOLD,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {aud(top ? top.amountCents : lot.openingCents)}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  color: '#cfd9e2',
                  fontSize: 13.5,
                  fontWeight: 600
                }}
              >
                {top ? bidderLabel(top.bidder) : 'No bids yet — be the first'}
              </Typography>
            </Box>
            <Box
              sx={{ borderLeft: { sm: `1px solid ${GOLD}33` }, pl: { sm: 3 } }}
            >
              <Typography
                sx={{
                  font: mono,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#8ea3b3'
                }}
              >
                Bidders
              </Typography>
              <Typography
                className="inc-orbitron"
                sx={{
                  mt: 0.75,
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 800,
                  color: '#eef6fb',
                  lineHeight: 1
                }}
              >
                {view?.bidders ?? 0}
              </Typography>
              <Typography sx={{ mt: 0.75, color: '#8ea3b3', fontSize: 13.5 }}>
                steps of {aud(lot.minRaiseCents)}
              </Typography>
            </Box>
            <Box
              sx={{
                gridColumn: { xs: '1 / -1', sm: 'auto' },
                borderLeft: { sm: `1px solid ${GOLD}33` },
                pl: { sm: 3 }
              }}
            >
              <Typography
                sx={{
                  font: mono,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#8ea3b3',
                  mb: 0.75
                }}
              >
                {open ? 'Closes in' : 'Closed'}
              </Typography>
              {open ? (
                <AuctionCountdown
                  endsAt={lot.endsAt}
                  compact
                  tone={{
                    ink: '#eef6fb',
                    label: '#8ea3b3',
                    box: 'rgba(255,255,255,0.04)',
                    border: `${GOLD}33`
                  }}
                />
              ) : (
                <Typography sx={{ color: '#cfd9e2', fontSize: 14 }}>
                  {closes}
                </Typography>
              )}
              {open && (
                <Typography sx={{ mt: 0.75, color: '#8ea3b3', fontSize: 12.5 }}>
                  {closes} · a bid in the last{' '}
                  {Math.round(lot.extensionSeconds / 60)} min extends it
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            <Button
              component={Link}
              to="/member-portal/physical-cards"
              endIcon={open ? <GavelIcon /> : <ArrowForwardIcon />}
              sx={{
                borderRadius: '4px',
                px: 3,
                py: 1.3,
                fontWeight: 800,
                letterSpacing: '0.04em',
                textTransform: 'none',
                color: '#0b0f14',
                background: GOLD,
                boxShadow: `0 10px 30px -12px ${GOLD}`,
                '&:hover': { background: '#f0d58c' }
              }}
            >
              {open ? 'Sign in to bid' : 'See the auction'}
            </Button>
            <Typography
              sx={{
                color: '#8ea3b3',
                fontSize: 12.5,
                lineHeight: 1.5,
                flex: '1 1 240px'
              }}
            >
              Bidding needs {lot.depositInc.toLocaleString('en-AU')} INC in your
              balance, held as a deposit while you are in the top three and
              released when you are not.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuctionHomeSection;
